import { supabase } from '../lib/supabase';
import type { Influencer, InfluencerInsert, InfluencerUpdate } from '../types/influencer';
import { log } from '../lib/logger';
import { createCrudService } from '../lib/createCrudService';

const crud = createCrudService<Influencer, InfluencerInsert, InfluencerUpdate>('influencers', {
  orderBy: 'full_name',
  ascending: true,
});

export const influencerService = {
  ...crud,

  async update(id: string, influencer: InfluencerUpdate): Promise<Influencer> {
    const updateData = { ...influencer, updated_at: new Date().toISOString() };

    log.debug('InfluencerService.update - Sending to Supabase:', {
      id,
      updateData,
      default_commission_pct: updateData.default_commission_pct,
    });

    const { data, error } = await supabase
      .from('influencers')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      log.error('InfluencerService.update - Supabase error:', error);
      throw error;
    }

    log.debug('InfluencerService.update - Success, received:', {
      id: data.id,
      default_commission_pct: data.default_commission_pct,
    });

    return data;
  },

  async getByName(fullName: string): Promise<Influencer | null> {
    const { data, error } = await supabase
      .from('influencers')
      .select('*')
      .ilike('full_name', fullName)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  async searchByName(term: string): Promise<Influencer[]> {
    const { data, error } = await supabase
      .from('influencers')
      .select('*')
      .ilike('full_name', `%${term}%`)
      .order('full_name')
      .limit(8);

    if (error) throw error;
    return data || [];
  },
};
